import { CodeCell, MutedBadge } from "@/components/commerce/grid-cells";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { OrderService } from "@/gen/omnios/v1/order_connect";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { createClient } from "@connectrpc/connect";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/returns/")({
	component: ReturnsListPage,
});

const client = createClient(OrderService, transport);

function ReturnsListPage() {
	const { t } = useTranslation("commerce");
	const { onGridReady, onStateChanged } = useGridLayout("commerce-returns");
	const { selectedRows, onSelectionChanged, ActionBar } = useGridSelection();
	const [orderId, setOrderId] = useState("");
	const [submittedOrderId, setSubmittedOrderId] = useState("");

	useBreadcrumbs([{ label: t("returns_title") }]);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<any>({
		fetchRows: async ({ pageSize, pageToken }) => {
			if (!submittedOrderId) return { rows: [], totalCount: 0 };
			const resp = await client.listReturns({
				orderId: submittedOrderId,
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.returns,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter: submittedOrderId,
		colToField: {
			seqNo: "seq_no",
			statusDisplayName: "status_display_name",
			refundAmountCents: "refund_amount_cents",
			createdAt: "created_at",
		},
	});

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "seqNo",
				headerName: "#",
				width: 70,
				pinned: "left",
				cellClass: "font-mono",
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return null;
					return (
						<a
							href={`/commerce/returns/${p.data.id}`}
							className="text-primary hover:underline"
						>
							{String(p.data.seqNo)}
						</a>
					);
				},
			},
			{
				field: "reason",
				headerName: t("return_reason"),
				flex: 2,
				minWidth: 150,
				valueFormatter: (p) => p.value || "\u2014",
			},
			{
				field: "resolution",
				headerName: t("return_resolution"),
				width: 130,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return null;
					const label = t(`resolution_${p.value}`, { defaultValue: p.value });
					return <CodeCell value={label} />;
				},
			},
			{
				field: "statusDisplayName",
				headerName: t("return_status"),
				width: 130,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return null;
					return <MutedBadge label={p.value} />;
				},
			},
			{
				field: "refundAmountCents",
				headerName: t("return_amount"),
				width: 120,
				type: "rightAligned",
				cellClass: "font-tabular",
				filter: "agNumberColumnFilter",
				valueFormatter: (p) =>
					p.value != null ? formatCurrency(p.value) : "\u2014",
			},
			{
				field: "createdAt",
				headerName: t("date_col"),
				width: 120,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => {
					const ts = p.data?.createdAt;
					return ts
						? new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU")
						: "\u2014";
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	// Status bar
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "выбрано" })}`
						: `${totalRows} ${t("returns_title", { defaultValue: "возвратов" })}`,
				align: "left" as const,
			},
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			{/* Search bar */}
			<div className="flex items-center gap-2 px-4 py-2">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("returns_search_placeholder")}
						value={orderId}
						onChange={(e) => setOrderId(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && orderId.trim()) {
								setSubmittedOrderId(orderId.trim());
							}
						}}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					size="sm"
					className="h-9"
					onClick={() => setSubmittedOrderId(orderId.trim())}
					disabled={!orderId.trim()}
				>
					<Search className="h-4 w-4 mr-1" />
					{t("returns_search_btn", { defaultValue: "Найти" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("menu_refresh")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "returns.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
			</div>

			{/* Grid */}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id}
					overlayNoRowsTemplate={
						!submittedOrderId
							? `<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("returns_title")}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("returns_search_hint", { defaultValue: "Введите ID заказа для просмотра возвратов" })}</p></div>`
							: `<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_returns", { defaultValue: "Нет возвратов" })}</p></div>`
					}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "Загрузка" })}...</span>`}
				/>
			</div>
			<ActionBar />
		</div>
	);
}
